// sim/creatureSprite.ts — the CREATURE SPRITE ASSEMBLER: turn the engine's
// EFFECT_CHANNEL vector (the gene/genome visual seam) into a compact, layered
// 2D sprite SPEC that a real renderer would draw. The engine is headless, so we
// emit DATA (part scales/colours/variants + attachment presence/size/colour),
// never pixels.
//
// WHAT A "SPRITE" IS HERE (data-oriented mandate, DESIGN.md §2 / EFFICIENCY.md §B):
//   A CreatureSprite is a small bundle of typed arrays — NOT a tree of part
//   objects. Assembly writes into a caller-owned (reused) CreatureSprite; nothing
//   allocates per call. Thousands of organisms that share a genome SNAPSHOT share
//   ONE cached CreatureSprite (SpriteCache), and per-entity individuality is a
//   cheap read-time hash (vari*), never a re-assembly.
//
// THE SEAM IT CONSUMES:
//   genes.ts / genome.ts already reduce a creature to an EFFECT_CHANNEL vector
//   (Movement / Combat / Sensing / Defense / Toxicity / Regen / Size). This file
//   owns the OTHER half of the rendering seam genome.ts hinted at ("size …
//   reserved for future rendering"): how that channel vector maps onto visible
//   body parts + attachments. Same input ⇒ identical sprite (pure integer math,
//   no RNG, fixed iteration), so a genome edit that changes the channels changes
//   the look — deterministically.

import {
  EFFECT_CHANNEL,
  BODY_PART,
  BODY_PART_COUNT,
  ATTACHMENT,
  ATTACHMENT_COUNT,
  SPRITE_PALETTE_SIZE,
  CASTE,
  CASTE_COUNT,
} from '../types.ts';
import type { Caste } from '../types.ts';
import type { CasteProfile } from './genome.ts';
import { fnvInit, fnvU32 } from '../hash.ts';

// ---------------------------------------------------------------------------
// Tunable visual-mapping constants. ALL INTEGER so sprites stay hashable and
// deterministic. Thresholds gate whether a feature appears; *_GAIN constants are
// per-channel-unit growth; *_STEP constants drive discrete stacking (more plates,
// etc.). Scales are PERCENT of a baseline (100 = baseline) and clamp to
// [SCALE_MIN, SCALE_MAX] so a runaway genome can't produce absurd geometry.
// ---------------------------------------------------------------------------

/** Baseline scale percent (100% = the renderer's nominal part size). */
const SCALE_BASE = 100;
/** Hard clamp on any part/overall scale percent (keeps geometry sane). */
const SCALE_MIN = 50;
const SCALE_MAX = 300;
/** Hard clamp on any attachment size percent. */
const ATTACH_SIZE_MIN = 40;
const ATTACH_SIZE_MAX = 300;

// --- Size channel → overall + core-part scale ---
/** Each +1 Size grows the overall sprite by this many percent. */
const SIZE_GAIN = 8;
/** Fraction (percent points per Size unit) the abdomen tracks above baseScale. */
const ABDOMEN_SIZE_GAIN = 4;

// --- Combat channel → mandibles + head ---
/** Combat at/above this threshold mounts mandibles. */
const MAND_T = 1;
/** Mandible pair count once present (a left + right jaw). */
const MAND_COUNT = 2;
/** Each +1 Combat grows the mandibles by this many percent. */
const MAND_GAIN = 14;
/** Each +1 Combat grows the head by this many percent (aggressive profile). */
const HEAD_COMBAT_GAIN = 5;
/** Combat at/above this threshold flips the head to its "aggressive" variant. */
const HEAD_AGGRO_T = 3;

// --- Defense channel → armour plates + thorax ---
/** Defense at/above this threshold mounts armour. */
const ARMOR_T = 1;
/** Each ARMOR_STEP of Defense adds one more stacked plate. */
const ARMOR_STEP = 2;
/** Each +1 Defense grows each plate by this many percent. */
const ARMOR_GAIN = 10;
/** Each +1 Defense grows the thorax by this many percent. */
const THORAX_DEFENSE_GAIN = 5;

// --- Toxicity channel → venom sac (+ a luminous glow) ---
/** Toxicity at/above this threshold mounts a venom sac. */
const VENOM_T = 1;
/** Sac base count, plus one more per VENOM_STEP of Toxicity. */
const VENOM_STEP = 3;
/** Each +1 Toxicity grows the sac by this many percent. */
const VENOM_GAIN = 12;
/**
 * Each +1 Toxicity adds this much VENOM-GLOW intensity (an abstract 0..GLOW_MAX
 * brightness the renderer turns into a luminous halo on the sac). Integer so the
 * sprite stays hashable; the concrete hue lives in the renderer (sickly green/
 * yellow), keeping this channel a pure intensity.
 */
const VENOM_GLOW_GAIN = 18;

// --- Regen channel → bioluminescent body glow (+ the existing colour tint) ---
/**
 * Each +1 Regen adds this much BODY-GLOW (bioluminescence) intensity — a 0..GLOW_MAX
 * brightness the renderer paints as a soft cyan/teal aura on the abdomen. Integer +
 * hue-agnostic for the same hashability reason as VENOM_GLOW_GAIN.
 */
const BODY_GLOW_GAIN = 16;

/** Hard clamp on either glow intensity (keeps the aura legible, never blinding). */
const GLOW_MAX = 200;

// --- Movement channel → limbs + wings ---
/** Each +1 Movement lengthens the limbs by this many percent. */
const LIMB_GAIN = 9;
/** Movement at/above this grants wings (matches genome FLY_THRESHOLD intent). */
const FLY_T = 6;
/** Wing pair count once present. */
const WING_COUNT = 2;
/** Each +1 Movement grows the wings by this many percent. */
const WING_GAIN = 10;

// --- Sensing channel → antennae ---
/** Sensing at/above this threshold mounts antennae. */
const ANT_T = 1;
/** Each +1 Sensing grows the antennae by this many percent. */
const ANT_GAIN = 11;

// --- Regen channel → colour tint only (structure unchanged) ---
/** Each REGEN_STEP of Regen shifts part colour one step toward the living hue. */
const REGEN_STEP = 2;
/** Palette index the Regen tint walks toward (a "living"/green-ish slot). */
const REGEN_HUE = 6;

// --- Per-entity variation bounds (read-time, NON-structural) ---
/** Max +/- palette-index jitter an individual's colour may take. */
const VARI_COLOR_SPREAD = 3;
/** Number of distinct cosmetic shape sub-variants an individual may take. */
const VARI_VARIANT_SPAN = 4;

// ---------------------------------------------------------------------------
// Caste base look — so even a ZERO-gene caste is visually distinct. Applied
// FIRST; channel deltas add on top. Indexed by CASTE.*.
// ---------------------------------------------------------------------------

/** Base palette index (the caste's signature colour). Length CASTE_COUNT. */
const CASTE_PALETTE: readonly number[] = [
  2, // Worker     — earthy
  9, // Soldier    — dark/red
  12, // Flyer      — pale/airy
  4, // Specialist — toxic/violet
];

/**
 * Base part scale percents per caste: [Abdomen, Thorax, Head, Limbs]. Gives each
 * caste a recognisable silhouette before any gene applies. Length CASTE_COUNT,
 * inner length BODY_PART_COUNT (ordered by BODY_PART.*).
 */
const CASTE_BODY: readonly (readonly number[])[] = [
  // Abd  Tho  Head Limb
  [110, 100, 95, 105], // Worker:     plump body, sturdy legs
  [105, 120, 115, 100], // Soldier:    broad thorax + big head
  [85, 95, 90, 130], // Flyer:      slim body, long legs
  [115, 100, 105, 100], // Specialist: heavy abdomen (glands)
];

/**
 * Default attachments per caste, given as a [size%] per ATTACHMENT.* slot; 0 =
 * absent. Lets a gene-free caste already show its role (Flyer has wings, Soldier
 * mandibles + armour, Specialist a venom sac). Channel deltas grow/add on top.
 * Length CASTE_COUNT, inner length ATTACHMENT_COUNT (ordered by ATTACHMENT.*).
 */
const CASTE_ATTACH: readonly (readonly number[])[] = [
  // Wings Armor Venom Mand Ant
  [0, 0, 0, 0, 90], // Worker:     plain (faint antennae only)
  [0, 100, 0, 100, 0], // Soldier:    baseline armour + mandibles
  [120, 0, 0, 0, 110], // Flyer:      wings + keen antennae
  [0, 0, 100, 0, 90], // Specialist: baseline venom sac
];

/** Default mandible/wing PAIR counts a caste shows when its attachment is present. */
const CASTE_PAIR_COUNT = 2;

// ---------------------------------------------------------------------------
// Small integer helpers (no allocation; shared by the assembler).
// ---------------------------------------------------------------------------

function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Wrap an integer into [0, SPRITE_PALETTE_SIZE) (palette indices are modular). */
function wrapPalette(v: number): number {
  const m = v % SPRITE_PALETTE_SIZE;
  return m < 0 ? m + SPRITE_PALETTE_SIZE : m;
}

/** Positive part of a (possibly signed) channel value. */
function pos(v: number): number {
  return v > 0 ? v : 0;
}

/**
 * Deterministic per-entity integer hash (splitmix32 finalizer, same family as
 * rng.ts but PURE — no state). Used to derive read-time cosmetic variation from
 * a (seed, salt) pair without any allocation or RNG object.
 */
function mixSeed(seed: number, salt: number): number {
  let z = (((seed >>> 0) + Math.imul(salt + 1, 0x9e3779b9)) >>> 0) >>> 0;
  z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
  z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
  return (z ^ (z >>> 15)) >>> 0;
}

// ---------------------------------------------------------------------------
// CreatureSprite — the compact, reusable, layered sprite SPEC. All columns are
// integer typed arrays so the whole sprite hashes cleanly and copies cheaply.
// ---------------------------------------------------------------------------

export class CreatureSprite {
  // --- Body parts (index by BODY_PART.*). ---
  /** Part scale, percent of baseline (100 = baseline). */
  readonly partScale: Int32Array;
  /** Part palette colour index in [0, SPRITE_PALETTE_SIZE). */
  readonly partColor: Int32Array;
  /** Small cosmetic shape-variant id per part (renderer picks a mesh/tile). */
  readonly partVariant: Int32Array;

  // --- Attachments (index by ATTACHMENT.*). ---
  /** Instance count; 0 = the attachment is absent. */
  readonly attachCount: Int32Array;
  /** Attachment size, percent of baseline. */
  readonly attachSize: Int32Array;
  /** Attachment palette colour index in [0, SPRITE_PALETTE_SIZE). */
  readonly attachColor: Int32Array;

  // --- Overall ---
  /** Overall sprite scale percent (multiplies the whole creature). */
  baseScale: number;
  /** The caste signature palette index this sprite was built from. */
  basePalette: number;

  // --- Glow channels (render-only luminance cues; 0 = no glow). ---
  /**
   * Luminous-halo intensity on the VENOM SAC, 0..GLOW_MAX. Driven by Toxicity:
   * 0 ⇒ no glow (a non-toxic creature is matte). The hue (sickly green/yellow)
   * is the renderer's; this is a pure brightness so the spec stays integer-hashable.
   */
  venomGlow: number;
  /**
   * Bioluminescent-aura intensity on the BODY (abdomen), 0..GLOW_MAX. Driven by
   * Regen: 0 ⇒ no glow. Hue (cyan/teal) is the renderer's; this is brightness only.
   */
  bodyGlow: number;

  constructor() {
    this.partScale = new Int32Array(BODY_PART_COUNT);
    this.partColor = new Int32Array(BODY_PART_COUNT);
    this.partVariant = new Int32Array(BODY_PART_COUNT);
    this.attachCount = new Int32Array(ATTACHMENT_COUNT);
    this.attachSize = new Int32Array(ATTACHMENT_COUNT);
    this.attachColor = new Int32Array(ATTACHMENT_COUNT);
    this.baseScale = SCALE_BASE;
    this.basePalette = 0;
    this.venomGlow = 0;
    this.bodyGlow = 0;
  }

  /** Reset every field to an empty baseline (allocation-free). */
  reset(): void {
    this.partScale.fill(0);
    this.partColor.fill(0);
    this.partVariant.fill(0);
    this.attachCount.fill(0);
    this.attachSize.fill(0);
    this.attachColor.fill(0);
    this.baseScale = SCALE_BASE;
    this.basePalette = 0;
    this.venomGlow = 0;
    this.bodyGlow = 0;
  }

  /**
   * FNV-1a fingerprint over every field, in a fixed order. Equal sprites ⇒ equal
   * fingerprint (determinism tests + cache dedup). Allocation-free.
   */
  fingerprint(): number {
    let h = fnvInit();
    h = fnvU32(h, this.baseScale);
    h = fnvU32(h, this.basePalette);
    h = fnvU32(h, this.venomGlow);
    h = fnvU32(h, this.bodyGlow);
    for (let i = 0; i < BODY_PART_COUNT; i++) {
      h = fnvU32(h, this.partScale[i] as number);
      h = fnvU32(h, this.partColor[i] as number);
      h = fnvU32(h, this.partVariant[i] as number);
    }
    for (let i = 0; i < ATTACHMENT_COUNT; i++) {
      h = fnvU32(h, this.attachCount[i] as number);
      h = fnvU32(h, this.attachSize[i] as number);
      h = fnvU32(h, this.attachColor[i] as number);
    }
    return h >>> 0;
  }

  /** Copy every field into `dst` (for snapshotting into the cache). No alloc. */
  copyInto(dst: CreatureSprite): void {
    dst.partScale.set(this.partScale);
    dst.partColor.set(this.partColor);
    dst.partVariant.set(this.partVariant);
    dst.attachCount.set(this.attachCount);
    dst.attachSize.set(this.attachSize);
    dst.attachColor.set(this.attachColor);
    dst.baseScale = this.baseScale;
    dst.basePalette = this.basePalette;
    dst.venomGlow = this.venomGlow;
    dst.bodyGlow = this.bodyGlow;
  }
}

// ---------------------------------------------------------------------------
// THE MAPPING — assemble an EFFECT_CHANNEL vector + caste into a sprite spec.
// ---------------------------------------------------------------------------

/**
 * Fill `out` in place from a 7-long EFFECT_CHANNEL vector + caste, deterministic
 * and allocation-free. Order matters: the CASTE BASE LOOK is laid down first,
 * then each channel ADDS its deltas, then `variation` applies a non-structural
 * cosmetic nudge. `variation` is a per-entity seed (e.g. entity id) — DIFFERENT
 * seeds keep identical structure (same parts/attachments/scales) and differ only
 * in colour/variant. Pass 0 for the canonical (un-varied) profile sprite.
 */
export function assembleFromChannels(
  out: CreatureSprite,
  channels: readonly number[],
  caste: Caste,
  variation: number,
): void {
  const mv = channels[EFFECT_CHANNEL.Movement] ?? 0;
  const cb = channels[EFFECT_CHANNEL.Combat] ?? 0;
  const sn = channels[EFFECT_CHANNEL.Sensing] ?? 0;
  const df = channels[EFFECT_CHANNEL.Defense] ?? 0;
  const tx = channels[EFFECT_CHANNEL.Toxicity] ?? 0;
  const rg = channels[EFFECT_CHANNEL.Regen] ?? 0;
  const sz = channels[EFFECT_CHANNEL.Size] ?? 0;

  const mvp = pos(mv);
  const cbp = pos(cb);
  const snp = pos(sn);
  const dfp = pos(df);
  const txp = pos(tx);
  const rgp = pos(rg);

  // --- 1. Caste base look (so even zero-gene castes are distinct). ---
  const baseBody = CASTE_BODY[caste] as readonly number[];
  const basePal = CASTE_PALETTE[caste] as number;
  const baseAttach = CASTE_ATTACH[caste] as readonly number[];

  out.basePalette = basePal;
  // Glow channels start dark every call so a REUSED `out` buffer can't leak a
  // prior assembly's glow into a creature with no Toxicity/Regen (assembly
  // overwrites field-by-field rather than calling reset()).
  out.venomGlow = 0;
  out.bodyGlow = 0;
  for (let p = 0; p < BODY_PART_COUNT; p++) {
    out.partScale[p] = baseBody[p] as number;
    out.partColor[p] = basePal;
    out.partVariant[p] = 0;
  }
  for (let a = 0; a < ATTACHMENT_COUNT; a++) {
    const baseSize = baseAttach[a] as number;
    if (baseSize > 0) {
      // Wings + mandibles come in pairs; the rest are a single instance.
      out.attachCount[a] =
        a === ATTACHMENT.Wings || a === ATTACHMENT.Mandibles ? CASTE_PAIR_COUNT : 1;
      out.attachSize[a] = baseSize;
      out.attachColor[a] = basePal;
    } else {
      out.attachCount[a] = 0;
      out.attachSize[a] = 0;
      out.attachColor[a] = basePal;
    }
  }

  // --- 2. Size → overall + core-part scale. ---
  let baseScale = SCALE_BASE + sz * SIZE_GAIN;
  baseScale = clampInt(baseScale, SCALE_MIN, SCALE_MAX);
  out.baseScale = baseScale;
  // Abdomen/thorax/head track overall size; abdomen carries it most (the gut).
  out.partScale[BODY_PART.Abdomen] = clampInt(
    (out.partScale[BODY_PART.Abdomen] as number) + sz * (SIZE_GAIN + ABDOMEN_SIZE_GAIN),
    SCALE_MIN,
    SCALE_MAX,
  );
  out.partScale[BODY_PART.Thorax] = clampInt(
    (out.partScale[BODY_PART.Thorax] as number) + sz * SIZE_GAIN,
    SCALE_MIN,
    SCALE_MAX,
  );
  out.partScale[BODY_PART.Head] = clampInt(
    (out.partScale[BODY_PART.Head] as number) + sz * SIZE_GAIN,
    SCALE_MIN,
    SCALE_MAX,
  );

  // --- 3. Combat → mandibles + bigger, aggressive head. ---
  if (cb >= MAND_T) {
    const grow = cbp * MAND_GAIN;
    if ((out.attachCount[ATTACHMENT.Mandibles] as number) === 0) {
      out.attachCount[ATTACHMENT.Mandibles] = MAND_COUNT;
      out.attachSize[ATTACHMENT.Mandibles] = clampInt(
        SCALE_BASE + grow,
        ATTACH_SIZE_MIN,
        ATTACH_SIZE_MAX,
      );
    } else {
      out.attachSize[ATTACHMENT.Mandibles] = clampInt(
        (out.attachSize[ATTACHMENT.Mandibles] as number) + grow,
        ATTACH_SIZE_MIN,
        ATTACH_SIZE_MAX,
      );
    }
  }
  out.partScale[BODY_PART.Head] = clampInt(
    (out.partScale[BODY_PART.Head] as number) + cbp * HEAD_COMBAT_GAIN,
    SCALE_MIN,
    SCALE_MAX,
  );
  if (cb >= HEAD_AGGRO_T) out.partVariant[BODY_PART.Head] = 1; // "aggressive" head

  // --- 4. Defense → stacked armour plates + broader thorax. ---
  if (df >= ARMOR_T) {
    const plates = 1 + Math.floor(dfp / ARMOR_STEP);
    const grow = dfp * ARMOR_GAIN;
    const baseExisting = out.attachSize[ATTACHMENT.Armor] as number;
    out.attachCount[ATTACHMENT.Armor] =
      (out.attachCount[ATTACHMENT.Armor] as number) + plates;
    out.attachSize[ATTACHMENT.Armor] = clampInt(
      (baseExisting > 0 ? baseExisting : SCALE_BASE) + grow,
      ATTACH_SIZE_MIN,
      ATTACH_SIZE_MAX,
    );
  }
  out.partScale[BODY_PART.Thorax] = clampInt(
    (out.partScale[BODY_PART.Thorax] as number) + dfp * THORAX_DEFENSE_GAIN,
    SCALE_MIN,
    SCALE_MAX,
  );

  // --- 5. Toxicity → venom sac (a toxic hue). ---
  if (tx >= VENOM_T) {
    const count = 1 + Math.floor(txp / VENOM_STEP);
    const grow = txp * VENOM_GAIN;
    const baseExisting = out.attachSize[ATTACHMENT.VenomSac] as number;
    out.attachCount[ATTACHMENT.VenomSac] =
      (out.attachCount[ATTACHMENT.VenomSac] as number) + count;
    out.attachSize[ATTACHMENT.VenomSac] = clampInt(
      (baseExisting > 0 ? baseExisting : SCALE_BASE) + grow,
      ATTACH_SIZE_MIN,
      ATTACH_SIZE_MAX,
    );
    out.attachColor[ATTACHMENT.VenomSac] = TOXIC_HUE;
    // The sac GLOWS: brighter with Toxicity, clamped so it never blinds. Only a
    // toxic creature lights up — a zero-Toxicity creature keeps venomGlow at 0.
    out.venomGlow = clampInt(txp * VENOM_GLOW_GAIN, 0, GLOW_MAX);
  }

  // --- 6. Movement → longer limbs + wings. ---
  out.partScale[BODY_PART.Limbs] = clampInt(
    (out.partScale[BODY_PART.Limbs] as number) + mvp * LIMB_GAIN,
    SCALE_MIN,
    SCALE_MAX,
  );
  const wantWings = mv >= FLY_T || caste === CASTE.Flyer;
  if (wantWings) {
    const grow = mvp * WING_GAIN;
    if ((out.attachCount[ATTACHMENT.Wings] as number) === 0) {
      out.attachCount[ATTACHMENT.Wings] = WING_COUNT;
      out.attachSize[ATTACHMENT.Wings] = clampInt(
        SCALE_BASE + grow,
        ATTACH_SIZE_MIN,
        ATTACH_SIZE_MAX,
      );
    } else {
      out.attachSize[ATTACHMENT.Wings] = clampInt(
        (out.attachSize[ATTACHMENT.Wings] as number) + grow,
        ATTACH_SIZE_MIN,
        ATTACH_SIZE_MAX,
      );
    }
  }

  // --- 7. Sensing → antennae. ---
  if (sn >= ANT_T) {
    const grow = snp * ANT_GAIN;
    if ((out.attachCount[ATTACHMENT.Antennae] as number) === 0) {
      out.attachCount[ATTACHMENT.Antennae] = 1;
      out.attachSize[ATTACHMENT.Antennae] = clampInt(
        SCALE_BASE + grow,
        ATTACH_SIZE_MIN,
        ATTACH_SIZE_MAX,
      );
    } else {
      out.attachSize[ATTACHMENT.Antennae] = clampInt(
        (out.attachSize[ATTACHMENT.Antennae] as number) + grow,
        ATTACH_SIZE_MIN,
        ATTACH_SIZE_MAX,
      );
    }
  }

  // --- 8. Regen → bioluminescent body glow + a "living" colour tint (structure
  //        unchanged). The glow ramps from ANY positive Regen (a smooth aura);
  //        the discrete colour-walk still needs a couple of Regen to step a slot.
  if (rgp > 0) {
    // Bioluminescence: the body lights up, brighter with Regen, clamped. A
    // zero-Regen creature keeps bodyGlow at 0 (matte tissue).
    out.bodyGlow = clampInt(rgp * BODY_GLOW_GAIN, 0, GLOW_MAX);
    const shift = Math.floor(rgp / REGEN_STEP);
    if (shift > 0) {
      for (let p = 0; p < BODY_PART_COUNT; p++) {
        out.partColor[p] = tintToward(out.partColor[p] as number, REGEN_HUE, shift);
      }
    }
  }

  // --- 9. Per-entity cosmetic variation (NON-structural; read-time-equivalent). ---
  if (variation !== 0) {
    applyVariation(out, variation);
  }
}

/** Toxic colour hue (a venom-sac palette slot). Module const for clarity. */
const TOXIC_HUE = 4;

/**
 * Walk a palette index `from` `steps` positions toward `target` (saturating at
 * the target), then wrap into palette range. Pure integer, allocation-free.
 */
function tintToward(from: number, target: number, steps: number): number {
  const f = wrapPalette(from);
  if (f === target) return f;
  const dir = target > f ? 1 : -1;
  const dist = Math.abs(target - f);
  const moved = f + dir * Math.min(steps, dist);
  return wrapPalette(moved);
}

// ---------------------------------------------------------------------------
// Per-entity variation — cheap, deterministic, NON-structural cosmetic jitter.
// These let the renderer make individuals look slightly different WITHOUT
// re-assembling or allocating: thousands of organisms share ONE cached sprite
// and call vary* at draw time. Structure (which parts/attachments are present
// and their scales) is identical across seeds; only colour/variant shift.
// ---------------------------------------------------------------------------

/**
 * Varied palette colour for body part `part` of a base sprite, given a per-entity
 * `seed`. Same seed ⇒ same colour; jitter bounded by VARI_COLOR_SPREAD. Pure +
 * allocation-free. The base sprite is NOT mutated.
 */
export function variColor(base: CreatureSprite, part: number, seed: number): number {
  const h = mixSeed(seed, part);
  const span = VARI_COLOR_SPREAD * 2 + 1;
  const delta = (h % span) - VARI_COLOR_SPREAD;
  return wrapPalette((base.partColor[part] as number) + delta);
}

/**
 * Varied cosmetic shape-variant for body part `part`, given a per-entity `seed`.
 * Returns a small id in [0, VARI_VARIANT_SPAN). Pure + allocation-free; does not
 * change which parts/attachments exist (structure is fixed by assembly).
 */
export function variVariant(base: CreatureSprite, part: number, seed: number): number {
  const h = mixSeed(seed, part + BODY_PART_COUNT);
  // Bias by the base variant so an "aggressive" head still reads aggressive.
  return ((base.partVariant[part] as number) + (h % VARI_VARIANT_SPAN)) % VARI_VARIANT_SPAN;
}

/**
 * Bake the read-time variation INTO `out` (used by assembleFromChannels when a
 * non-zero variation seed is supplied, and by tests). Keeps structure identical
 * to the un-varied sprite — only partColor/partVariant move. Allocation-free.
 */
function applyVariation(out: CreatureSprite, seed: number): void {
  for (let p = 0; p < BODY_PART_COUNT; p++) {
    out.partColor[p] = variColor(out, p, seed);
    out.partVariant[p] = variVariant(out, p, seed);
  }
}

// ---------------------------------------------------------------------------
// Profile bridge — derive an EFFECT_CHANNEL vector from a resolved CasteProfile,
// so a sprite can be assembled straight from a genome snapshot (what spawns
// capture) without re-deriving the gene channel sum. The mapping inverts the
// salient profile fields back to the channels that visibly shaped them.
// ---------------------------------------------------------------------------

/** Baseline used to recover a "Defense-ish" channel from a profile's hp. */
const PROFILE_HP_BASE = 8;
/** Baseline used to recover a "Sensing-ish" channel from a profile's senseRadius. */
const PROFILE_SENSE_BASE = 5;
/** Divisor turning a profile's attackPower into a Combat channel magnitude. */
const PROFILE_ATTACK_DIV = 1;
/** Extra Movement granted when a profile is flying (wings). */
const PROFILE_FLY_MOVE = 6;

/**
 * Fill `out` (length EFFECT_CHANNEL_COUNT) with an EFFECT_CHANNEL vector derived
 * from `profile`. Deterministic, allocation-free (writes the caller's array).
 * Note this is a VISUAL approximation: profile fields are lossy (the genome
 * folded several channels together), so this recovers the salient look, not the
 * exact original channel sum.
 */
export function channelsFromProfile(profile: CasteProfile, out: number[]): void {
  out[EFFECT_CHANNEL.Movement] =
    Math.max(0, (profile.stepsPerTick - 1) * 2) + (profile.flying ? PROFILE_FLY_MOVE : 0);
  out[EFFECT_CHANNEL.Combat] = Math.floor(profile.attackPower / PROFILE_ATTACK_DIV);
  out[EFFECT_CHANNEL.Sensing] = Math.max(0, profile.senseRadius - PROFILE_SENSE_BASE);
  out[EFFECT_CHANNEL.Defense] = Math.max(0, Math.floor((profile.startHp - PROFILE_HP_BASE) / 2));
  out[EFFECT_CHANNEL.Toxicity] = Math.max(0, profile.toxicity);
  out[EFFECT_CHANNEL.Regen] = Math.max(0, profile.regen);
  out[EFFECT_CHANNEL.Size] = Math.max(0, profile.size);
}

// ---------------------------------------------------------------------------
// SpriteCache — the performance model. Assemble ONCE per genome snapshot id
// (profileRef); every entity stamped with that snapshot shares the SAME cached
// CreatureSprite instance. Snapshots in genome.ts are append-only + immutable,
// so an entry never needs invalidation. Per-entity variation stays OUT (it's the
// cheap vari* read-time path), so thousands of organisms cost ONE assembly each.
// ---------------------------------------------------------------------------

export class SpriteCache {
  /** Cached sprite per profileRef, or null if not yet assembled. Index = profileRef. */
  private readonly slots: (CreatureSprite | null)[];
  /** Reused scratch channel vector for the profile bridge (no per-call alloc). */
  private readonly scratchChannels: number[];
  /** Scratch sprite assembled into before snapshotting into a slot (no per-call alloc). */
  private readonly scratch: CreatureSprite;

  /** How many real assemblies have happened (tests: == distinct profiles, NOT entities). */
  assembleCount = 0;
  /** How many getOrAssemble calls hit an existing cached sprite. */
  hitCount = 0;

  /**
   * @param maxProfiles upper bound on distinct snapshot ids (cache is sized to
   *   this; slots allocate lazily on first use). Genome snapshot count is the
   *   natural bound.
   */
  constructor(maxProfiles: number) {
    if (maxProfiles <= 0) throw new Error('SpriteCache maxProfiles must be > 0');
    this.slots = new Array<CreatureSprite | null>(maxProfiles).fill(null);
    this.scratchChannels = new Array<number>(7).fill(0);
    this.scratch = new CreatureSprite();
  }

  /** Capacity (distinct profileRefs the cache can hold). */
  get capacity(): number {
    return this.slots.length;
  }

  /**
   * Return the SHARED cached sprite for `profileRef`, assembling it once on first
   * request (from `profile` + `caste`) and snapshotting a copy into the slot.
   * Subsequent calls return the same instance with no work. The cached sprite is
   * the CANONICAL (variation=0) one — callers apply per-entity variation via the
   * vari* helpers at read time, NOT here.
   */
  getOrAssemble(profileRef: number, profile: CasteProfile, caste: Caste): CreatureSprite {
    const existing = this.slots[profileRef] ?? null;
    if (existing) {
      this.hitCount++;
      return existing;
    }
    // First request for this snapshot: assemble into scratch, then snapshot.
    channelsFromProfile(profile, this.scratchChannels);
    assembleFromChannels(this.scratch, this.scratchChannels, caste, 0);
    const stored = new CreatureSprite();
    this.scratch.copyInto(stored);
    this.slots[profileRef] = stored;
    this.assembleCount++;
    return stored;
  }

  /** Whether `profileRef` already has a cached sprite (no assembly side-effect). */
  has(profileRef: number): boolean {
    return (this.slots[profileRef] ?? null) !== null;
  }
}

// ---------------------------------------------------------------------------
// Convenience: assemble straight from a profile (bridge → channels → sprite),
// reusing a caller-owned scratch channel array. For one-off / demo / test use.
// ---------------------------------------------------------------------------

/**
 * Assemble `out` from a `profile` + `caste` (+ optional per-entity `variation`),
 * using `scratchChannels` (length >= 7, caller-owned) as the channel buffer so
 * nothing allocates. Equivalent to channelsFromProfile → assembleFromChannels.
 */
export function assembleForProfile(
  out: CreatureSprite,
  profile: CasteProfile,
  caste: Caste,
  variation: number,
  scratchChannels: number[],
): void {
  channelsFromProfile(profile, scratchChannels);
  assembleFromChannels(out, scratchChannels, caste, variation);
}

// Re-export caste count so example/tests can iterate without re-importing types.
export { CASTE, CASTE_COUNT };
